import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import socketService from '../services/socket';
import { sessionApi } from '../services/api';
import FileUpload from '../components/FileUpload';
import FileList from '../components/FileList';
import TextArea from '../components/TextArea';
import SessionHeader from '../components/SessionHeader';
import SessionNotice from '../components/SessionNotice';
import './Session.css';

function Session() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userCount, setUserCount] = useState(0);
  const [files, setFiles] = useState([]);
  const [text, setText] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [sessionCreated, setSessionCreated] = useState(false);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [openQRCodeInitially] = useState(
    () => location.state?.openQRCode === true
  );
  
  const socketRef = useRef(null);

  // Consume the navigation flag so refreshing the session does not reopen the QR code.
  useEffect(() => {
    if (location.state?.openQRCode) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  // Initialize socket connection and join session
  useEffect(() => {
    let isSubscribed = true;
    
    const initSession = async () => {
      try {
        // Open the session. If it was already cleaned up, the server re-creates
        // it under the same code - that is what keeps bookmarked session URLs
        // working. `created` tells us whether we started from scratch.
        const result = await sessionApi.ensureSession(sessionId);

        // Only proceed if component is still mounted
        if (!isSubscribed) return;

        setSessionCreated(result.created === true);

        // Connect to WebSocket
        socketRef.current = socketService.connect();
        
        // Set up event listeners
        setupSocketListeners();
        
        // Join the session
        socketService.joinSession(sessionId);
        
        // Load QR code
        loadQRCode();
      } catch (err) {
        console.error('Error initializing session:', err);
        if (!isSubscribed) return;

        if (err.response?.status === 400) {
          setError('Ungültiger Session-Code');
          setRedirecting(true);
          setTimeout(() => navigate('/'), 2000);
        } else if (err.response?.status === 429) {
          setError('Zu viele Anfragen. Bitte lade die Seite in einer Minute neu.');
        } else {
          setError('Fehler beim Verbinden zur Session');
        }
      }
    };

    initSession();

    // Cleanup on unmount
    return () => {
      isSubscribed = false;
      if (socketRef.current) {
        socketService.leaveSession();
        socketService.removeAllListeners();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, navigate]);

  const setupSocketListeners = useCallback(() => {
    // Session joined successfully
    socketService.onSessionJoined((data) => {
      console.log('Session joined:', data);
      setFiles(data.files || []);
      setText(data.text ? data.text.content : '');
      setUserCount(data.userCount || 0);
      setLoading(false);
    });

    // Session not found (only reachable if the session vanished between
    // opening it and the socket connecting)
    socketService.onSessionNotFound(() => {
      setError('Session nicht gefunden');
      setRedirecting(true);
      setTimeout(() => navigate('/'), 2000);
    });

    // User count updated
    socketService.onUserCountUpdated((count) => {
      setUserCount(count);
    });

    // File uploaded
    socketService.onFileUploaded((file) => {
      setFiles(prev => [...prev, file]);
    });

    // File removed (after download)
    socketService.onFileRemoved((data) => {
      setFiles(prev => prev.filter(f => f.id !== data.fileId));
    });

    // Text updated
    socketService.onTextUpdated((textData) => {
      setText(textData.content);
    });

    // Error
    socketService.onError((data) => {
      console.error('Socket error:', data);
      setError(data.message || 'Ein Fehler ist aufgetreten');
    });
  }, [navigate]);

  const loadQRCode = async () => {
    try {
      const response = await sessionApi.getQRCode(sessionId);
      if (response.success) {
        setQrCode(response.qrCode);
      }
    } catch (err) {
      console.error('Error loading QR code:', err);
    }
  };

  // Stable identity: the notice restarts its countdown whenever this changes,
  // and this component re-renders on every socket event.
  const handleNoticeDismiss = useCallback(() => setNoticeDismissed(true), []);

  const handleFileUpload = async (file, onProgress) => {
    try {
      const socketId = socketService.getSocketId();
      await sessionApi.uploadFile(sessionId, file, socketId, onProgress);
    } catch (err) {
      console.error('Error uploading file:', err);
      throw err;
    }
  };

  const handleTextSend = (content) => {
    // Send empty string if content is empty, to sync deletions
    socketService.sendText(sessionId, content || '');
  };

  const handleLeaveSession = () => {
    if (window.confirm('Möchtest du die Session wirklich verlassen?')) {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="session-loading">
        <div className="loading-spinner-large"></div>
        <p>Verbinde zur Session...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="session-error">
        <div className="error-icon">⚠️</div>
        <h2>{error}</h2>
        {redirecting && <p>Du wirst zur Startseite weitergeleitet...</p>}
      </div>
    );
  }

  return (
    <div className="session">
      <SessionHeader
        sessionId={sessionId}
        userCount={userCount}
        qrCode={qrCode}
        openQRCodeInitially={openQRCodeInitially}
        onLeave={handleLeaveSession}
      />

      <div className="container">
        {/* Shown when the code was re-opened after the old session expired */}
        {sessionCreated && !noticeDismissed && (
          <SessionNotice onDismiss={handleNoticeDismiss}>
            <strong>Neue Session unter diesem Code gestartet.</strong>
            <span>
              Die vorherige Session war nicht mehr aktiv – Texte und Dateien
              von damals sind daher nicht mehr vorhanden.
            </span>
          </SessionNotice>
        )}

        {/* Text Area - Full Width */}
        <div className="card card-text">
          <h2 className="section-title">
            <span>💬</span>
            Text
          </h2>
          <TextArea 
            onSend={handleTextSend}
            initialText={text}
          />
        </div>

        {/* Files Section - Two Columns */}
        <div className="session-grid">
          <div className="card">
            <h2 className="section-title">
              <span>📤</span>
              Dateien hochladen
            </h2>
            <FileUpload onUpload={handleFileUpload} />
          </div>

          <div className="card">
            <h2 className="section-title">
              <span>📁</span>
              Verfügbare Dateien
              {files.length > 0 && <span className="badge">{files.length}</span>}
            </h2>
            <FileList files={files} sessionId={sessionId} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Session;
